/**
 * Camera + verdict surface. The chrome around the shared
 * `checkin/scan/engine.ts` mechanism: full-bleed `<video>`, the iOS
 * safe-area insets, `facingMode: { ideal: "environment" }`, and the
 * keydown-buffer HID input — no Firestore import here (the
 * io-in-view-or-state guard would flag it regardless). Every decoded QR
 * payload, HID scan, or operator retry is pushed into verdict.state.ts's
 * `scannedToken$`; verdict.sync.ts is the sole subscriber and the sole
 * writer of the verdict
 * `<verdict-splash-view>`/`<pending-admit-view>` render — see
 * verdict.sync.ts's header for why that makes an optimistic render here
 * structurally impossible. `<pending-admit-view>` supersedes the prior
 * `<ticket-sheet-view pending>` mount for manual mode's confirm-before-write
 * brake; `<ticket-sheet-view>` keeps its own other job (the read-only "see
 * it again" pull-up), unmounted here today.
 *
 * An external barcode gun is a first-class input, not a legacy branch: a
 * `keydown` buffer resets on any human-slow gap (60ms) and completes on
 * Enter, feeding the SAME `scannedToken$` a camera decode does — the
 * resolve pipeline cannot tell the two apart, by design.
 *
 * `sound.unlock()` fires once here, at camera start: by the time this view
 * mounts the operator has already tapped through sign-in and "Continue to
 * scanning" (real gestures), so the shared AudioContext safely resumes here
 * rather than paying construction + an async resume on the very first
 * scan's own tone.
 *
 * `<door-log>` is mounted along the bottom edge, in THIS surface's own
 * stacking context — never through `show()`. It replaces the counts pill and
 * the two sheets that pill opened, which lived in the browser's top layer
 * above the splash and therefore had to close themselves on every resolving
 * verdict. This view also owns the journal's single write path: it is the one
 * component both the door PWA and the admin console mount, so one leg of the
 * pipeline below feeds both instead of two copies of the same derivation in
 * two workspaces.
 *
 * The console takes the door console's arrival with it: this surface now
 * spans its own bottom edge full-bleed and grows upward as the operator opens
 * the log, so it leaves no free corner an embedding shell can absolutely
 * position a control over. The `leading` slot at the head of the top row is
 * what it offers instead — a real allocation inside the layout this view
 * owns, rather than a guarantee about which corner it happens to leave empty
 * this month. A door phone is the whole app and slots nothing.
 */
import { sound, tooltip } from "@mhmo91/schmancy";
import { SchmancyElement, schmancyElement } from "@mhmo91/schmancy/mixins";
import { show } from "@mhmo91/schmancy/overlay";
import { computed } from "@mhmo91/schmancy/state";
import { type MessageKey, translate } from "@momo/i18n";
import {
	acceptedTypeIds,
	type CameraErrorKind,
	checkinMode,
	createScanEngine,
	hasUndoPermission,
	originFilter,
	type ScanEngine,
	scannedToken$,
	typeFilterIsAutomatic,
	type Verdict,
	verdictState,
} from "@momo/views/checkin";
import { css, html } from "lit";
import { state } from "lit/decorators.js";
import { keyed } from "lit/directives/keyed.js";
import { createRef, ref } from "lit/directives/ref.js";
import { when } from "lit/directives/when.js";
import {
	defer,
	exhaustMap,
	filter,
	finalize,
	from,
	fromEvent,
	map,
	merge,
	scan as rxScan,
	Subject,
	skip,
	takeUntil,
	tap,
	timer,
} from "rxjs";
import { LocalizeMixin } from "../../i18n/localize.ts";
import { hasReportsPermission } from "../checkin.state.ts";
import { nameScan, recordScan } from "../door-log/door-log.state.ts";
import "../door-log/door-log.view.ts";
import { eventOptions, selectedEvent } from "../event/event.state.ts";
import { answeredOrders } from "../manual-lookup/manual-lookup.state.ts";
import ManualLookupView from "../manual-lookup/manual-lookup.view.ts";
import { ticketTypeNames } from "../ticket-types/ticket-types.state.ts";
import { answeredTickets } from "../tickets/tickets.state.ts";
import { holderName, scannedHolder } from "../verdict/verdict.state.ts";

// A human typing (even fast) leaves gaps far above this; a barcode-gun's
// keystrokes land far below it — the threshold that tells the two apart.
const HID_HUMAN_SLOW_GAP_MS = 60;
const HID_MIN_LENGTH = 5;
const HID_ACTIVE_BADGE_MS = 1200;

type HidAccum = {
	readonly buffer: string;
	readonly lastAt: number;
	readonly emit: string | null;
};

const CAMERA_ERROR_COPY: Record<
	CameraErrorKind,
	{ icon: string; title: MessageKey; body: MessageKey }
> = {
	notAllowed: {
		icon: "no_photography",
		title: "scanner.scan.cameraError.notAllowed.title",
		body: "scanner.scan.cameraError.notAllowed.body",
	},
	notFound: {
		icon: "videocam_off",
		title: "scanner.scan.cameraError.notFound.title",
		body: "scanner.scan.cameraError.notFound.body",
	},
	notReadable: {
		icon: "block",
		title: "scanner.scan.cameraError.notReadable.title",
		body: "scanner.scan.cameraError.notReadable.body",
	},
	overconstrained: {
		icon: "tune",
		title: "scanner.scan.cameraError.overconstrained.title",
		body: "scanner.scan.cameraError.overconstrained.body",
	},
	decoderUnavailable: {
		icon: "refresh",
		title: "scanner.scan.cameraError.decoderUnavailable.title",
		body: "scanner.scan.cameraError.decoderUnavailable.body",
	},
};

// What this door admits, as names an operator reads rather than the ids the
// filter is stored as. An empty acceptedTypeIds accepts every type
// (settings.state.ts), which is the whole catalog — a list, never a void, so
// it is expanded here rather than treated as an absence. The door line reads
// the single-type case off it by name; settings owns the full list.
const doorAcceptedNames = computed<readonly string[]>(() => {
	const accepted = acceptedTypeIds.get();
	const names = ticketTypeNames.get();
	if (accepted.length === 0) return names.map((t) => t.name);
	return names.filter((t) => accepted.includes(t.id)).map((t) => t.name);
});

@schmancyElement("scan-view")
export default class ScanView extends LocalizeMixin(SchmancyElement) {
	static styles = [
		css`
			:host {
				position: relative;
				display: block;
				height: 100%;
				overscroll-behavior: none;
				touch-action: none;
				user-select: none;
				-webkit-user-select: none;
				/* position:relative alone doesn't establish a stacking context —
				 * without isolation, a z-indexed child here escapes into an
				 * ancestor's own layering instead of staying scoped to this host. */
				isolation: isolate;
			}
		`,
	];

	@state() private cameraError: CameraErrorKind | null = null;
	@state() private retryingCamera = false;
	@state() private torchAvailable = false;
	@state() private torchOn = false;
	@state() private changingTorch = false;
	@state() private torchFailed = false;
	private readonly torchRequested = new Subject<void>();
	@state() private hidActive = false;
	/** Whether the door console below is expanded. Drives one branch — the idle
	 *  hint's own — because that hint tells the operator to point the camera at
	 *  a ticket, which is not what they are doing while they read the log. */
	@state() private logOpen = false;

	private videoRef = createRef<HTMLVideoElement>();
	private engine!: ScanEngine;

	override connectedCallback() {
		super.connectedCallback();

		this.engine = createScanEngine({
			getVideo: () => this.videoRef.value,
			constraints: {
				video: {
					facingMode: { ideal: "environment" },
					width: { ideal: 1280 },
					height: { ideal: 720 },
				},
			},
			ready: () => this.updateComplete,
		});

		// Pre-warms the shared AudioContext (construction + resume) against
		// the gestures the sign-in/event-pick flow already spent, so the
		// first scan's own tone doesn't pay that cost late — see this file's
		// own header.
		sound.unlock();

		const cameraEvents$ = this.engine.events$.pipe(
			tap((event) => {
				switch (event.kind) {
					case "cameraError":
						if (event.error !== null || !this.retryingCamera)
							this.cameraError = event.error;
						if (event.error !== null) this.retryingCamera = false;
						break;
					case "torchAvailable":
						this.retryingCamera = false;
						this.cameraError = null;
						this.torchAvailable = event.available;
						break;
					case "torchOn":
						this.torchOn = event.on;
						break;
					case "token":
						scannedToken$.next(event.token);
						break;
				}
			}),
		);

		const hidScan$ = fromEvent<KeyboardEvent>(document, "keydown").pipe(
			rxScan(
				(acc: HidAccum, e: KeyboardEvent): HidAccum => {
					const now = performance.now();
					const buffer =
						now - acc.lastAt < HID_HUMAN_SLOW_GAP_MS ? acc.buffer : "";
					if (e.key === "Enter") {
						return {
							buffer: "",
							lastAt: now,
							emit: buffer.length >= HID_MIN_LENGTH ? buffer : null,
						};
					}
					if (e.key.length === 1)
						return { buffer: buffer + e.key, lastAt: now, emit: null };
					return { buffer, lastAt: now, emit: null };
				},
				{ buffer: "", lastAt: 0, emit: null } as HidAccum,
			),
			filter((acc): acc is HidAccum & { emit: string } => acc.emit !== null),
			tap((acc) => {
				this.hidActive = true;
				timer(HID_ACTIVE_BADGE_MS)
					.pipe(takeUntil(this.disconnecting))
					.subscribe(() => {
						this.hidActive = false;
					});
				scannedToken$.next(acc.emit);
			}),
		);

		// The journal's whole write path. It lives here rather than in either
		// app's `.sync` because `door-log.state.ts` has no backing to reconcile
		// against — it is a derivation over the verdict stream — and because
		// this view is the one component both consumers mount, so one leg feeds
		// the door phone and the admin console instead of two copies drifting
		// apart in two workspaces. `fe-state` names exactly this home: the
		// pipeline that populates a cell lives in the owning view's
		// connectedCallback unless a routing decision waits on it, and nothing
		// routes on the journal.
		//
		// `pending-admit` is deliberately NOT recorded: the redeem write has not
		// happened yet, so a hold the operator then declines would sit in the
		// journal reading "Admitted" — a lie on the one surface built to settle
		// disputes. Its own commit lands as a real `admit` a tap later.
		//
		// The two `.value` reads are point-in-time snapshots of what this door
		// knew at the instant of the scan, which is what a journal entry means —
		// not a derivation that has to stay live.
		const journal$ = merge(
			verdictState.$.pipe(
				// Skips the cell's own replay of whatever verdict was already
				// standing when this view connected: that scan is either already
				// journalled or was taken before this session.
				skip(1),
				map((s) => s.verdict),
				filter(
					(verdict): verdict is Verdict =>
						verdict !== null &&
						verdict.kind !== "confirming" &&
						verdict.kind !== "pending-admit",
				),
				tap((verdict) => {
					const ticket = "ticket" in verdict ? verdict.ticket : null;
					recordScan({
						key: crypto.randomUUID(),
						atMillis: Date.now(),
						ticketId: ticket?.ticketId ?? null,
						// Already settled only when this same ticket was scanned
						// before; the ordinary case is null here and patched below.
						holderName: holderName.get(),
						// Free on the console, where the Orders tab's own listener
						// already holds every order. Empty on a door phone, whose
						// order index opens only while the lookup screen is —
						// `buyer.sync.ts` pays one bounded read per scan there
						// instead, and patches the row when it settles.
						buyerName:
							ticket === null || !hasReportsPermission.get()
								? null
								: (answeredOrders.get()?.byOrderId.get(ticket.orderId)
										?.buyerName ?? null),
						verdict,
					});
				}),
			),
			// The holder read settles AFTER the verdict it belongs to
			// (`verdict.state.ts`'s own header), so the name arrives once the row
			// is already standing. Keyed on the ticket id the read was performed
			// for, never on "the row currently on top" — the same equality guard
			// `holderName` applies before the splash renders a name.
			scannedHolder.$.pipe(
				filter((holder) => holder !== null),
				tap((holder) => {
					nameScan(holder.ticketId, {
						holderName: holder.name,
						buyerName: null,
					});
				}),
			),
		);

		merge(
			cameraEvents$,
			hidScan$,
			journal$,
			this.torchRequested.pipe(
				exhaustMap(() =>
					defer(() => from(this.engine.toggleTorch())).pipe(
						tap((accepted) => {
							this.torchFailed = !accepted;
						}),
						finalize(() => {
							this.changingTorch = false;
						}),
					),
				),
			),
		)
			.pipe(takeUntil(this.disconnecting))
			.subscribe();
	}

	override disconnectedCallback() {
		super.disconnectedCallback();
		this.engine.stop();
	}

	/** Both Find guest controls open this. Suspend and resume ride one
	 *  subscription lifetime: `defer` puts the suspend on subscribe, `finalize`
	 *  the resume on every exit — complete, error, and the unsubscribe
	 *  `takeUntil` performs at disconnect. Flipping it at click time instead
	 *  would leave the scanner deaf with no overlay open whenever anything
	 *  between throws. */
	#openGuestLookup = () =>
		defer(() => {
			this.engine.suspendDecoding();
			// Solid opacity is real background contrast against the live camera
			// feed behind it: the default translucent fill reads unreadable over
			// moving video. No footprint is named here: the room this search needs
			// is declared on ManualLookupView's own `:host`, which the overlay
			// then clamps to the viewport.
			return show(ManualLookupView, {
				opacity: "solid",
				ariaLabel: translate(this.uiLanguage, "scanner.scan.findGuest"),
			});
		})
			.pipe(
				finalize(() => this.engine.resumeDecoding()),
				takeUntil(this.disconnecting),
			)
			.subscribe();

	override render() {
		const verdict = verdictState.value.verdict;
		const mode = checkinMode.value;
		const live = answeredTickets.get()?.live ?? false;
		const accepted = acceptedTypeIds.get();
		const acceptedNames = doorAcceptedNames.get();
		const origin = originFilter.value;
		// One accepted type reads as its own name — shorter than counting to
		// one, and the name is the fact the operator wants. The count is read
		// off the filter rather than off the resolved names, so a catalog that
		// hasn't landed yet still states how many types this door is set to
		// instead of collapsing to "all".
		const types =
			acceptedNames.length === 1
				? (acceptedNames[0] ?? "")
				: translate(this.uiLanguage, "scanner.scan.door.types", {
						count: accepted.length,
					});
		// A moving word for a moving lane, and nothing at all for a fixed set:
		// a derived lane re-reads itself at every entry-window boundary, so at
		// 02:00 "Now: 2 ticket types" and a bare "2 ticket types" say
		// different things about what this door admits at 02:01. The operator's
		// own pick carries no prefix — a bare count is what every door has
		// always printed, so it is the learned default rather than a second
		// signal to read. Absent where the door admits everything: no lane, so
		// nothing to name. When the pick was taken belongs to the settings
		// panel, which has the measure for it; this line owes the state alone,
		// and the worst case already runs to 46 characters beside the mode.
		const typeSummary =
			accepted.length === 0
				? translate(this.uiLanguage, "scanner.scan.door.allTypes")
				: typeFilterIsAutomatic.get()
					? translate(this.uiLanguage, "scanner.scan.door.lane", {
							when: translate(this.uiLanguage, "scanner.scan.door.now"),
							types,
						})
					: types;
		// `all` is the unfiltered default: naming it would spend a third of the
		// line saying no filter is set.
		const doorLine = [
			translate(
				this.uiLanguage,
				mode === "auto"
					? "scanner.scan.status.modeAuto"
					: "scanner.scan.status.modeManual",
			),
			typeSummary,
			...(origin === "all"
				? []
				: [
						translate(
							this.uiLanguage,
							origin === "guest"
								? "scanner.settings.origin.guests"
								: "scanner.settings.origin.sales",
						),
					]),
		].join(" · ");

		return html`
			<div class="relative h-full w-full overflow-hidden bg-black">
				<video
					${ref(this.videoRef)}
					class="absolute inset-0 h-full w-full object-cover"
					autoplay
					muted
					playsinline
					aria-hidden="true"
				></video>

				${when(this.cameraError, (error) => {
					const copy = CAMERA_ERROR_COPY[error];
					return html`
				<div class="absolute inset-0 h-full w-full overflow-hidden bg-black" ?hidden=${verdict !== null || this.logOpen}>
					<div class="flex h-full w-full flex-col items-center justify-center gap-4 p-6 text-center">
						<schmancy-icon size="48" class="text-error-default">${copy.icon}</schmancy-icon>
						<schmancy-typography type="headline" token="sm">${translate(this.uiLanguage, copy.title)}</schmancy-typography>
						<schmancy-typography type="body" token="md" class="max-w-xs text-surface-onVariant">
							${translate(this.uiLanguage, copy.body)}
						</schmancy-typography>
						${when(
							error === "decoderUnavailable",
							() => html`
						<schmancy-button variant="filled" @click=${() => location.reload()}>
							<schmancy-icon slot="prefix">refresh</schmancy-icon>
							${translate(this.uiLanguage, "scanner.scan.cameraError.reload")}
						</schmancy-button>`,
							() => html`
						<schmancy-button variant="filled" .busy=${this.retryingCamera} @click=${() => {
							if (this.retryingCamera) return;
							this.retryingCamera = true;
							this.engine.retry();
						}}>
							<schmancy-icon slot="prefix">restart_alt</schmancy-icon>
							${translate(this.uiLanguage, "scanner.scan.cameraError.tryAgain")}
						</schmancy-button>`,
						)}
					</div>
				</div>
			`;
				})}

				<!-- An external scanner still needs the door controls and verdicts when the camera fails. -->
				<!-- Installed on an iPhone (viewport-fit=cover + a black-translucent
				     status bar, index.html), the layout viewport runs under the
				     Dynamic Island. The camera bleeding behind it is the point;
				     chrome sitting there is not — iOS neither paints nor delivers
				     taps into that strip. So every edge-anchored control in this
				     view adds the matching inset to its own offset, left and right
				     included for a phone held in landscape. Off iOS each env()
				     resolves to 0px and nothing moves. -->
				<div
					class="pointer-events-none absolute inset-x-0 top-0 flex items-center justify-between gap-2 pb-4 pt-[calc(1rem+env(safe-area-inset-top))] pl-[calc(1rem+env(safe-area-inset-left))] pr-[calc(1rem+env(safe-area-inset-right))]"
				>
					<div class="flex min-w-0 items-center gap-2">
<!-- The way off this screen, for a consumer that has one: the
						     admin console slots its back button here, a door phone is
						     the whole app and slots nothing. It is a slot rather than a
						     free corner because this view leaves none — the door console
						     spans the bottom edge and grows upward as the operator opens
						     it. An unassigned slot renders no box (display:contents is
						     the UA default), so the gap beside it collapses and a door
						     phone's readout sits where it always has; pointer-events
						     inherits through that missing box, carrying auto past the
						     row's own none. -->
						<slot name="leading" class="pointer-events-auto"></slot>
						<!-- The door's own readout, on the same glass the confirming pill is
						     made of: chrome floating over a live camera. It answers which event
						     this phone is scanning and what this entrance admits — complete for
						     one accepted type or for all of them; past two, the caption gives the
						     count and settings carries the names.
						     Mode, types and source ride under the event name as one caption line:
						     three stacked rows would eat the top of the camera for a setting made
						     once a shift. Offline sits level with that line rather than under it,
						     so a live alarm the operator has to act on never loses to a preference
						     set at the top of a shift, and an alarm earns the line it takes when it
						     wraps. min-w-0 because the status words at their German widths plus the
						     control group overrun a 393px phone: status is a readout and gives,
						     buttons are the only way out of this screen and never do.
						     tune sits in the pill because it opens the sheet that owns the full
						     accepted-type list and the origin row this caption summarises. text,
						     not outlined: the glass already paints the material and a ring inside
						     it is a second boundary on the same corner. pr-1 because the 48px
						     button carries its own optical padding; py-1 keeps it at 48px rather
						     than squeezing it. -->
						<schmancy-surface
							type="containerHighest"
							opacity="glass"
							elevated
							class="pointer-events-auto flex min-w-0 items-center gap-2 rounded-2xl py-1 pl-4 pr-1"
						>
							<div class="flex min-w-0 flex-wrap items-center gap-x-4 gap-y-1">
								<div class="flex min-w-0 flex-col">
									<!-- Clamped, because an event name is operator-typed and
									     unbounded: the line beneath it is composed from a closed
									     vocabulary and can be reasoned about at its German width, but
									     a festival's own name cannot, and an un-clamped one pushes the
									     button group past the right edge — the buttons being the only
									     way off this screen. Two lines rather than one: these names run
									     venue-plus-edition long, and clamping that to one line ellipses
									     the half that distinguishes tonight from every other night at
									     the same door. -->
									<schmancy-typography type="title" token="sm" maxLines="2">
										${selectedEvent.get()?.name ?? translate(this.uiLanguage, "scanner.scan.door.noEvent")}
									</schmancy-typography>
									<schmancy-typography type="label" token="xs" class="text-surface-onVariant">
										${doorLine}
									</schmancy-typography>
								</div>
								${when(
									!live,
									() => html`
										<schmancy-typography type="title" token="sm" class="text-error-default">
											${translate(this.uiLanguage, "scanner.scan.status.networkOffline")}
										</schmancy-typography>
									`,
								)}
								${when(
									this.hidActive,
									() => html`
										<schmancy-typography type="label" token="md" class="text-surface-onVariant">
											${translate(this.uiLanguage, "scanner.scan.status.externalScanner")}
										</schmancy-typography>
									`,
								)}
							</div>
							<schmancy-icon-button
								icon="tune"
								variant="text"
								title=${translate(this.uiLanguage, "scanner.scan.settings")}
								aria-label=${translate(this.uiLanguage, "scanner.scan.settings")}
								@click=${() =>
									// Suspend and resume ride one subscription lifetime:
									// `defer` puts the suspend on subscribe, `finalize` the
									// resume on every exit — complete, error, and the
									// unsubscribe `takeUntil` performs at disconnect. Flipping
									// it at click time instead would leave the scanner deaf
									// with no overlay open whenever anything between throws.
									defer(() => {
										this.engine.suspendDecoding();
										// Same solid-opacity treatment as the lookup overlay
										// below, for the same reason: this too opens over the
										// live camera feed. Factory form (not the SettingsView
										// class form) because this door's own event pick — a
										// scanner-app-local fact SettingsView reads as props,
										// not state (see settings.view.ts's own header) — is
										// read fresh at mount time, so the overlay's inputs
										// bind when the template renders rather than freezing
										// at definition.
										return show(
											() => html`
												<checkin-settings-view
													.eventName=${selectedEvent.get()?.name ?? null}
													.canChangeEvent=${(eventOptions.value?.length ?? 0) > 1}
												></checkin-settings-view>
											`,
											{
												opacity: "solid",
												ariaLabel: translate(
													this.uiLanguage,
													"scanner.scan.settings",
												),
											},
										);
									})
										.pipe(
											finalize(() => this.engine.resumeDecoding()),
											takeUntil(this.disconnecting),
										)
										.subscribe()}
							></schmancy-icon-button>
						</schmancy-surface>
					</div>
					<!-- Camera controls and the way into a guest lookup; the door's own
					     settings open from the pill that states them. -->
					<div class="pointer-events-auto flex shrink-0 items-center gap-2">
						${when(
							this.torchAvailable,
							() => html`
								<schmancy-icon-button
									variant="outlined"
									title=${translate(this.uiLanguage, this.torchFailed ? "scanner.scan.torchFailed" : this.torchOn ? "scanner.scan.torchOff" : "scanner.scan.torchOn")}
									aria-label=${translate(this.uiLanguage, this.torchFailed ? "scanner.scan.torchFailed" : this.torchOn ? "scanner.scan.torchOff" : "scanner.scan.torchOn")}
									color=${this.torchFailed ? "error" : "primary"}
									${tooltip(translate(this.uiLanguage, this.torchFailed ? "scanner.scan.torchFailed" : this.torchOn ? "scanner.scan.torchOff" : "scanner.scan.torchOn"))}
									.icon=${this.torchFailed ? "refresh" : this.torchOn ? "flash_on" : "flash_off"}
									.busy=${this.changingTorch}
									@click=${() => {
										if (this.changingTorch) return;
										this.changingTorch = true;
										this.torchRequested.next();
									}}
								></schmancy-icon-button>
							`,
						)}
						<!-- Two nodes for one control, swapped at the sm rung. Filled tonal
						     on both: over a live moving camera feed an opaque ground is what
						     stays readable. inline-flex, not block — block would override the
						     display both primitives declare on :host. No aria-label on the
						     labelled form: its visible label IS the accessible name. -->
						<schmancy-icon-button
							class="sm:hidden"
							icon="person_search"
							variant="filled tonal"
							title=${translate(this.uiLanguage, "scanner.scan.findGuest")}
							aria-label=${translate(this.uiLanguage, "scanner.scan.findGuest")}
							@click=${this.#openGuestLookup}
						></schmancy-icon-button>
						<schmancy-button class="hidden sm:inline-flex" variant="filled tonal" @click=${this.#openGuestLookup}>
							<schmancy-icon slot="prefix" aria-hidden="true">person_search</schmancy-icon>
							${translate(this.uiLanguage, "scanner.scan.findGuest")}
						</schmancy-button>
					</div>
				</div>

				${when(
					verdict === null && !this.logOpen && !this.cameraError,
					() => html`
						<!-- Clears the door console AT REST — an opened one grows past
						     any fixed offset, which is why the branch above drops this
						     hint entirely rather than trying to sit above it. -->
						<div
							class="pointer-events-none absolute inset-x-0 bottom-[calc(7rem+env(safe-area-inset-bottom))] flex justify-center px-4"
						>
							<schmancy-surface type="containerLow" opacity="solid" class="rounded-2xl p-4 text-center">
								<schmancy-typography type="body" token="md">
									${translate(this.uiLanguage, "scanner.scan.idle")}
								</schmancy-typography>
							</schmancy-surface>
						</div>
					`,
				)}

				<!-- The door console, mounted INSIDE this surface's own stacking
				     context rather than through show(). That is the whole structural
				     point of it: a show() overlay lives in the browser's top layer,
				     above the verdict splash's z-40, so the two sheets this replaces
				     had to close themselves on every resolving verdict or the
				     operator would be scanning blind behind them — losing the dispute
				     surface at the exact moment a queue forms behind the person
				     arguing. Here a verdict simply paints over it and the operator
				     keeps reading. Nothing about this surface may go back through
				     show().
				     Rendered after the idle hint above: neither carries a z-index, so
				     paint order is DOM order here, and an opened console grows upward
				     well past that hint's offset — first in the template, the hint
				     showed through the journal. The splash still wins over both, at
				     z-40, which outranks paint order.
				     The wrapper carries the console's own surface colour so the iOS
				     home-indicator inset below it is painted rather than leaking the
				     camera through a strip, and clips the disclosure at 82% so an
				     opened journal can never cover the whole viewfinder. -->
				<div
					class="pointer-events-auto absolute inset-x-0 bottom-0 grid max-h-[82%] min-h-0 overflow-hidden rounded-t-3xl bg-surface-containerLowest pb-[env(safe-area-inset-bottom)]"
				>
					<door-log
						class="[--door-log-journal:440px]"
						@toggle=${(e: CustomEvent<{ open: boolean }>) => {
							this.logOpen = e.detail.open;
						}}
					></door-log>
				</div>

				${when(
					verdict?.kind === "confirming",
					() => html`
						<div class="pointer-events-none absolute inset-x-0 top-1/3 flex justify-center">
							<schmancy-surface
								type="containerHighest"
								opacity="glass"
								elevated
								class="flex items-center gap-2 rounded-full px-4 py-2"
							>
								<schmancy-spinner size="sm"></schmancy-spinner>
								<schmancy-typography type="label" token="md">
									${translate(
										this.uiLanguage,
										(verdict as Extract<Verdict, { kind: "confirming" }>).slow
											? "scanner.verdict.confirming.slow"
											: "scanner.verdict.confirming.default",
									)}
								</schmancy-typography>
							</schmancy-surface>
						</div>
					`,
				)}

				${when(
					verdict !== null &&
						verdict.kind !== "confirming" &&
						verdict.kind !== "pending-admit",
					() =>
						keyed(
							verdict,
							html`<verdict-splash-view .canUndo=${hasUndoPermission.get()}></verdict-splash-view>`,
						),
				)}

				${when(verdict?.kind === "pending-admit", () => html`<pending-admit-view></pending-admit-view>`)}
			</div>
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		"scan-view": ScanView;
	}
}
